import { Labels } from '../../global/labels';
import { UIElement } from '../ui-element.js';
import { hide, rebuildChildren, show } from '../../global/ui-helpers';
import { setInnerText, createElement } from '../../global/render-api';
import { dispatchNativeEvent } from '../../global/events';
import { EventObserver } from '../../global/event-observer';
import { Digest } from '../../global/digest';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { UIUploadModes, UIUploadMode } from './ui-upload.type';
import type {
    AttributeValue,
    ObservedAttributes,
    ElementConfig,
    ElementChild,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-upload.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIUpload
 * @element ui-upload
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-upload</code> element.
 * Provides input field with file upload user interface.
 * @fires event:change
 * @listens event:remove-upload-item
 * @property {boolean} [multiple] {@attr multiple} If true, multiple files can be added.
 * @property {string} [controlId] {@attr control-id} Control (input element) id. Deprecated! Prefer setting id on the native input. Priority: native input id > control-id > uid.
 * @property {("add" | "replace")} [mode="add"] {@attr mode} Upload mode for multiple.
 *  {@desc add: each time file attached to the end of the list}
 *  {@desc replace: each time selected, files replace the previous list (native behaviour)}
 * @property {HTMLInputElement} input Shortcut to input element.
 * @property {HTMLInputElement} inputAux Shortcut to additional input element.
 * Needed for the multiple add mode (solved problem with duplicates).
 * @property {HTMLButtonElement} button Shortcut to upload button element.
 * @property {HTMLButtonElement} clearBtn Shortcut to clean button element.
 * @property {HTMLLabelElement} label Shortcut to label element.
 * @property {HTMLDivElement} placeholder Shortcut to placeholder.
 * @slot
 * @example
 * <ui-upload>
 *   <input type="text" name="fileupload" placeholder="Max 12 MB" id="test-ui-upload">
 * </ui-upload>
 */
@Component({ tag: 'ui-upload', styles })
export class UIUpload extends UIElement {
    /**
     * Mode of uploading.
     * @type {{ADD: string, REPLACE: string}}
     */
    static readonly MODES: UIUploadModes = {
        ADD: 'add',
        REPLACE: 'replace',
    } as const;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-upload', {
            upload: 'Upload',
            uploadFiles: 'Upload files',
            clear: 'Remove file',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['control-id'];
    }

    @Attribute(Boolean) multiple?: boolean;
    @Attribute(String) controlId?: string;
    @Attribute(String) mode: UIUploadMode = UIUpload.MODES.ADD;

    @Child('.ui-upload__input') input?: HTMLInputElement;
    @Child('.ui-upload__input-auxiliary') inputAux?: HTMLInputElement;
    @Child('.ui-upload__label') label?: HTMLLabelElement;
    @Child('.ui-upload__submit') button?: HTMLButtonElement;
    @Child('.ui-upload__clear') clearBtn?: HTMLButtonElement;
    @Child('.ui-upload__placeholder') placeholder?: HTMLDivElement;

    private _id?: string;
    private _filelist: Array<File> = [];
    private inProgress: boolean = false;
    private _hasUserSetNativeId: boolean = false;
    private readonly clickObserver = new EventObserver({ name: 'click' });

    private readonly fileChangeHandler = this.handleFileChange.bind(this);
    private readonly inputHandler = this.handleInput.bind(this);
    private readonly removeUploadItemHandler = this.handleRemoveUploadItem.bind(
        this
    ) as EventListener;
    private readonly clearHandler = this.reset.bind(this);

    get uid(): string {
        if (!this._id) {
            this._id = Digest.randomId();
        }
        return this._id;
    }

    /**
     * @inheritDoc
     */
    /**
     * Returns the control ID for ui-field (label[for], aria-describedby).
     * Priority: native input id > control-id > uid.
     * @returns {string}
     */
    getControlIdForField(): string {
        const controlIdAttr =
            this.getAttribute('control-id') || this.controlId || null;
        const inputEl =
            this.input ||
            this.querySelector<HTMLInputElement>('.ui-upload__input');
        if (inputEl) {
            const inputId = inputEl.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (inputEl.id !== controlIdAttr) {
                    inputEl.id = controlIdAttr;
                    inputEl.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = inputEl.id || this.uid;
            if (inputEl.id !== effectiveId) {
                inputEl.id = effectiveId;
                inputEl.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr || this.uid;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'control-id':
                if (this._hasUserSetNativeId || !this.input) {
                    break;
                }
                if (newValue) {
                    this.input.id = newValue;
                    this.input.setAttribute('id', newValue);
                } else {
                    this.input.removeAttribute('id');
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @type {HTMLCollection<UIUploadItem>}
     */
    get uploadItems(): HTMLCollection {
        return this.getFileListElement().children;
    }

    /**
     * Get or create file list element.
     * @returns {HTMLDivElement}
     */
    getFileListElement(): HTMLDivElement {
        let el = this.querySelector<HTMLDivElement>('.ui-upload__file-list');
        if (!el) {
            el = createElement<HTMLDivElement>({
                tagName: 'div',
                classList: {
                    'ui-upload__file-list': true,
                },
            })!;
            this.appendChild(el);
        }
        return el;
    }

    /**
     * Updates native input with multiple files.
     * @private
     */
    private updateMultipleView(): void {
        this.syncFiles();

        rebuildChildren(
            this.getFileListElement(),
            this._filelist.map(
                (file, id): ElementConfig => ({
                    tagName: 'ui-upload-item',
                    attributes: {
                        'data-index': id,
                        'filename': file.name,
                        'filesize': this.formatFileSize(file.size),
                        'link': URL.createObjectURL(file) || null,
                    },
                })
            )
        );
    }

    /**
     * Format file size from bytes to human-readable value.
     * @param {number} size
     * @returns {string}
     * @private
     */
    private formatFileSize(size: number): string {
        if (!size) {
            return '0';
        }

        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(size) / Math.log(1024));

        return (
            parseFloat((size / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
        );
    }

    /**
     * Resets component to initial state
     */
    reset(): void {
        this._filelist = [];
        if (this.input) {
            this.input.value = '';
        }
        if (this.inputAux) {
            this.inputAux.value = '';
        }
        if (this.input) {
            dispatchNativeEvent(this.input, 'change', true);
            if (this.input.willValidate) {
                this.input.checkValidity();
            }
        }
        this.button?.focus();
    }

    /**
     * Synchronise files from input and private variable
     */
    syncFiles(): void {
        const dt = new DataTransfer();
        if (this.inputAux) {
            this.inputAux.value = '';
        }
        for (const file of this._filelist) {
            dt.items.add(file);
        }

        // NB FileList interface on input is read-only. This is done like this
        // only to due to historical reasons.
        if (this.input) {
            this.input.files = dt.files;
            if (this.input.willValidate) {
                dispatchNativeEvent(this.input, 'invalid', true);
            }
        }
    }

    /**
     * Updates appearance regarding setting a file.
     */
    updateStatus(): void {
        if (this.multiple) {
            this.updateMultipleView();
        } else {
            if (this._filelist.length > 0) {
                if (this.placeholder) {
                    setInnerText(
                        this.placeholder,
                        this.input?.files?.[0]?.name || ''
                    );
                }
                if (this.clearBtn) {
                    show(this.clearBtn);
                }
            } else {
                if (this.placeholder) {
                    setInnerText(
                        this.placeholder,
                        this.input?.getAttribute('placeholder') || ''
                    );
                }
                if (this.clearBtn) {
                    hide(this.clearBtn);
                }
            }
        }
        this.updateClassList({
            '-empty': (this.input?.files?.length ?? 0) === 0,
        });
    }

    /**
     * Handle removing file item from list.
     * @param {CustomEvent} e
     */
    private handleRemoveUploadItem(
        e: CustomEvent<{ relatedTarget: HTMLElement }>
    ): void {
        const uploadItem = e.detail.relatedTarget?.closest('ui-upload-item');
        if (!uploadItem) {
            return;
        }
        const id = uploadItem.getAttribute('data-index');
        if (id) {
            this._filelist.splice(parseInt(id), 1);
            this.syncFiles();
            if (this.input) {
                dispatchNativeEvent(this.input, 'change', true);
                if (this.input.willValidate) {
                    this.input.checkValidity();
                }
            }
        }
    }

    /**
     * Fires callback when user changes file.
     * @private
     */
    private handleFileChange(_e?: Event): void {
        if (this.inProgress) {
            return;
        }

        const files: Array<File> =
            this.multiple && this.mode === UIUpload.MODES.ADD
                ? [...(this.inputAux?.files || [])]
                : [...(this.input?.files || [])];

        // In Safari browser such changes of this.input.files
        // emit native 'change' event once again.
        // this.inProgress = true - prevent 'change' in this case.
        this.inProgress = true;

        // Ensure _filelist is initialized
        if (!this._filelist) {
            this._filelist = [];
        }

        this._filelist =
            this.mode === UIUpload.MODES.REPLACE || !this.multiple
                ? files
                : this._filelist.concat(files);
        this.updateStatus();
        this.inProgress = false;
    }

    /**
     * Handle input activate.
     * @private
     */
    private handleInput(): void {
        if (!(this.multiple && this.mode === UIUpload.MODES.ADD)) {
            this.input?.click();
        } else {
            if (this.inputAux) {
                this.inputAux.value = '';
                this.inputAux.click();
            }
        }
    }

    /**
     * Renders clear button element.
     * @returns {ElementConfig}
     * @private
     */
    private renderClearButton(): ElementConfig {
        return {
            tagName: 'ui-button',
            attributes: {
                'class': 'ui-upload__clear',
                'layout': 'tertiary',
                'glyph': 'redesign-circle-x-mark',
                'aria-label': UIUpload.labels.clear,
                'disabled': this.hasAttribute('disabled'),
                'data-event': 'ui-upload-clear',
            },
        };
    }

    /**
     * @private
     * @ignore
     */
    getControlElement(): HTMLInputElement | undefined {
        return this.input;
    }

    /**
     * @private
     * @ignore
     */
    onValuePatched(_newValue: unknown): void {
        /* nop */
    }

    /**
     * @private
     * @ignore
     */
    onDisabledStateChanged(newState: boolean): void {
        if (this.input) {
            this.input.disabled = newState;
        }
        if (this.inputAux) {
            this.inputAux.disabled = newState;
        }
    }

    /**
     * @private
     * @ignore
     */
    onReadonlyStateChanged(newState: boolean): void {
        if (this.input) {
            this.input.readOnly = newState;
        }
        if (this.inputAux) {
            this.inputAux.readOnly = newState;
        }
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.input?.addEventListener('change', this.fileChangeHandler);
        this.inputAux?.addEventListener('change', this.fileChangeHandler);
        this.button?.addEventListener('click', this.inputHandler);
        this.clickObserver.observe(this);
        this.addEventListener(
            'remove-upload-item',
            this.removeUploadItemHandler
        );
        this.addEventListener('ui-upload-clear', this.clearHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.input?.removeEventListener('change', this.fileChangeHandler);
        this.inputAux?.removeEventListener('change', this.fileChangeHandler);
        this.button?.removeEventListener('click', this.inputHandler);
        this.clickObserver.disconnect();
        this.removeEventListener(
            'remove-upload-item',
            this.removeUploadItemHandler
        );
        this.removeEventListener('ui-upload-clear', this.clearHandler);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const input = this.queryChildren('input')[0] as
            | HTMLInputElement
            | undefined;
        const button = this.queryChildren('button')[0] as
            | HTMLButtonElement
            | undefined;
        this.detachChildNodes();
        this._filelist = input?.files ? [...input.files] : [];

        this._hasUserSetNativeId = !!input?.getAttribute('id')?.trim();

        let nativeId: NullOr<string>;
        if (input?.getAttribute('id')?.trim()) {
            nativeId = input.getAttribute('id')!.trim();
        } else if (this.getAttribute('control-id') || this.controlId) {
            nativeId =
                this.getAttribute('control-id') || this.controlId || null;
            if (input && nativeId) {
                input.id = nativeId;
                input.setAttribute('id', nativeId);
            }
        } else {
            nativeId = this.uid;
            if (input) {
                input.id = nativeId;
                input.setAttribute('id', nativeId);
            }
        }

        this.insertElements([
            {
                tagName: 'div',
                classList: {
                    'ui-upload__control-row': true,
                },
                children: (
                    [
                        this.multiple
                            ? {
                                  tagName: 'input',
                                  element: input,
                                  attributes: {
                                      type: 'file',
                                      multiple: this.multiple || null,
                                      id: nativeId,
                                  },
                                  classList: {
                                      'ui-upload__input': true,
                                      '-visually-hidden': true,
                                  },
                              }
                            : null,
                        this.multiple && this.mode === UIUpload.MODES.ADD
                            ? {
                                  tagName: 'input',
                                  attributes: {
                                      type: 'file',
                                      multiple: this.multiple,
                                      accept: input
                                          ? input.getAttribute('accept')
                                          : null,
                                  },
                                  classList: {
                                      'ui-upload__input-auxiliary': true,
                                      '-utility': true,
                                      '-hidden': true,
                                  },
                              }
                            : null,
                        !this.multiple
                            ? {
                                  tagName: 'div',
                                  classList: {
                                      'ui-upload__control': true,
                                  },
                                  children: [
                                      {
                                          tagName: 'input',
                                          element: input,
                                          attributes: {
                                              type: 'file',
                                              id: nativeId,
                                          },
                                          classList: {
                                              'ui-upload__input': true,
                                          },
                                      },
                                      {
                                          tagName: 'span',
                                          classList: {
                                              'ui-upload__label': true,
                                              'input-file': true,
                                          },
                                          children: [
                                              {
                                                  tagName: 'span',
                                                  classList: {
                                                      'ui-upload__placeholder': true,
                                                  },
                                                  children: input
                                                      ? [
                                                            input.getAttribute(
                                                                'placeholder'
                                                            ),
                                                        ]
                                                      : [],
                                              },
                                          ],
                                      },
                                      ...[this.renderClearButton()],
                                  ],
                              }
                            : null,
                        {
                            tagName: 'button',
                            element: button,
                            classList: {
                                'ui-upload__submit': true,
                                'button': true,
                                '-guiding': true,
                            },
                            attributes: {
                                type: 'button',
                            },
                            children: button
                                ? []
                                : this.multiple
                                  ? [UIUpload.labels.uploadFiles]
                                  : [UIUpload.labels.upload],
                        },
                    ] as Array<ElementChild>
                ).filter((c) => c !== null),
            },
        ]);

        this.updateStatus();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.inProgress = false;
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
